import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  forwardRef,
  ReactNode
} from "react";
import {OtsTooltip} from "./OtsTooltip";

/** Mirrors the intent-based ActionButtonStyle contract in ots-android-lib. */
export type OtsButtonVariant =
  | "primary"
  | "secondary"
  | "tonal"
  | "destructive"
  | "text"
  /** @deprecated Use `destructive`. */
  | "danger"
  /** @deprecated Use `text`. */
  | "ghost";

/** `mini` is for dense lists, e.g. actions on each row of a tray; `compact` for toolbars and compact mounts. */
export type OtsButtonSize = "mini" | "compact" | "default";

export type OtsActionButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: OtsButtonVariant;
  size?: OtsButtonSize;
  icon?: ReactNode;
  iconIsDecorative?: boolean;
};

const variantClasses: Record<OtsButtonVariant, string> = {
  primary: "border-[var(--ots-primary)] bg-[var(--ots-primary)] text-[var(--ots-on-primary)] hover:brightness-95",
  secondary: "border-[var(--ots-outline)] bg-[var(--ots-surface)] text-[var(--ots-primary)] hover:bg-[var(--ots-background)]",
  tonal: "border-transparent bg-[var(--ots-primary-container)] text-[var(--ots-primary)] hover:brightness-95",
  destructive: "border-[var(--ots-error)] bg-[var(--ots-error)] text-[var(--ots-on-error)] hover:brightness-95",
  text: "border-transparent bg-transparent text-[var(--ots-primary)] hover:bg-[var(--ots-primary-container)]",
  danger: "border-[var(--ots-error)] bg-[var(--ots-error)] text-[var(--ots-on-error)] hover:brightness-95",
  ghost: "border-transparent bg-transparent text-[var(--ots-primary)] hover:bg-[var(--ots-primary-container)]"
};

const sizeClasses: Record<OtsButtonSize, string> = {
  mini: "min-h-7 gap-1 px-2 py-0.5 text-xs",
  compact: "min-h-8 gap-2 px-2.5 py-1 text-sm",
  default: "min-h-10 gap-2 px-3.5 py-2 text-sm"
};

const iconSizeClasses: Record<OtsButtonSize, string> = {
  mini: "size-3.5",
  compact: "size-[18px]",
  default: "size-[18px]"
};

/** The action-button look, for elements that are not an `OtsActionButton` -- links, most often. */
export const getOtsActionButtonClassName = (
  variant: OtsButtonVariant = "secondary",
  size: OtsButtonSize = "default",
  className?: string
): string => [
  "inline-flex items-center justify-center rounded-lg border font-semibold transition-colors disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]",
  sizeClasses[size],
  variantClasses[variant],
  className
].filter(Boolean).join(" ");

/** Standard OTS action with shared sizing, focus, disabled, and intent styling. */
export const OtsActionButton = forwardRef<HTMLButtonElement, OtsActionButtonProps>(function OtsActionButton({
  variant = "secondary",
  size = "default",
  icon,
  iconIsDecorative = true,
  className,
  children,
  type = "button",
  ...props
}, ref) {
  return (
    <button
      ref={ref}
      type={type}
      className={getOtsActionButtonClassName(variant, size, className)}
      {...props}>
      {icon && <span aria-hidden={iconIsDecorative || undefined} className={[iconSizeClasses[size], "shrink-0 [&>svg]:size-full"].join(" ")}>{icon}</span>}
      {children}
    </button>
  );
});

export type OtsActionLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: OtsButtonVariant;
  size?: OtsButtonSize;
  icon?: ReactNode;
  /** Shown after the label, e.g. an external-link arrow. */
  trailingIcon?: ReactNode;
};

/** A link that looks like an OTS action button, for navigation that should read as an action. */
export const OtsActionLink = forwardRef<HTMLAnchorElement, OtsActionLinkProps>(function OtsActionLink({
  variant = "secondary",
  size = "default",
  icon,
  trailingIcon,
  className,
  children,
  ...props
}, ref) {
  return (
    <a ref={ref} className={getOtsActionButtonClassName(variant, size, className)} {...props}>
      {icon && <span aria-hidden="true" className={[iconSizeClasses[size], "shrink-0 [&>svg]:size-full"].join(" ")}>{icon}</span>}
      {children}
      {trailingIcon && <span aria-hidden="true" className={[size === "mini" ? "size-3" : "size-4", "shrink-0 [&>svg]:size-full"].join(" ")}>{trailingIcon}</span>}
    </a>
  );
});

export type OtsAsyncActionButtonProps = OtsActionButtonProps & {
  isLoading: boolean;
  loadingLabel?: ReactNode;
  progressLabel?: string;
};

/** Action button with the standard loading presentation and disablement. */
export const OtsAsyncActionButton = forwardRef<HTMLButtonElement, OtsAsyncActionButtonProps>(function OtsAsyncActionButton({
  isLoading,
  loadingLabel,
  progressLabel = "Action in progress",
  icon,
  children,
  disabled,
  ...props
}, ref) {
  const progress = (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      role="progressbar"
      aria-label={progressLabel}
      className="animate-spin">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity=".3"/>
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  );

  return (
    <OtsActionButton
      {...props}
      ref={ref}
      icon={isLoading ? progress : icon}
      iconIsDecorative={!isLoading}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}>
      {isLoading ? loadingLabel ?? children : children}
    </OtsActionButton>
  );
});

export type OtsIconActionButtonProps = Omit<OtsActionButtonProps, "children" | "icon"> & {
  icon: ReactNode;
  label: string;
  tooltip?: string;
  badge?: ReactNode;
};

/** Accessible icon-only action with the shared themed pointer/keyboard tooltip. */
export const OtsIconActionButton = forwardRef<HTMLButtonElement, OtsIconActionButtonProps>(function OtsIconActionButton({
  icon,
  label,
  tooltip = label,
  badge,
  size = "default",
  className,
  ...props
}, ref) {
  return <OtsTooltip content={tooltip} density={size === "default" ? "default" : "compact"}>
    <OtsActionButton
      {...props}
      ref={ref}
      size={size}
      aria-label={label}
      className={[
        size === "default" ? "!size-10 !min-h-0 !p-0" : size === "mini" ? "!size-7 !min-h-0 !p-0" : "!size-8 !min-h-0 !p-0",
        className
      ].filter(Boolean).join(" ")}>
      <span aria-hidden="true" className={["relative [&>svg]:size-full", iconSizeClasses[size]].join(" ")}>
        {icon}
        {badge && (
          <span className="absolute -right-2 -top-2 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-[var(--ots-accent)] px-1 text-[10px] font-bold leading-none text-white">
            {badge}
          </span>
        )}
      </span>
    </OtsActionButton>
  </OtsTooltip>;
});

/** @deprecated Prefer `OtsActionButton`; retained for incremental adoption. */
export const OtsButton = OtsActionButton;
export type OtsButtonProps = OtsActionButtonProps;
